import * as React from 'react';
import Stack from '@mui/material/Stack';
import RestrictedAccess from '../../components/RestrictedAccess';
import { useAppDispatch } from '../../hooks';
import { Button, Card, CardContent, TextField, Typography } from '@mui/material';
import ClassAutoComplete, { ClassProps } from '../../components/Autocomplete/CLDP/Class';
import DescriptionAutoComplete, {
  DescriptionProps,
} from '../../components/Autocomplete/CLDP/Description';
import LineAutoComplete, { LineProps } from '../../components/Autocomplete/CLDP/Line';
import catalogApiClient from '../../services/catalogapi';
import { ClassLineDescriptionProp } from './model';
import { AxiosResponse } from 'axios';
import { alertError, alertSuccess } from '../../slices/global/alertSlice';
import PartTypeAutoComplete, { PartTypeProps } from '../../components/Autocomplete/CLDP/PartType';

export default function ClassLineDescription() {
  const [sku, setSku] = React.useState<string>('');
  const [classId, setClassId] = React.useState<ClassProps | null>(null);
  const [lineId, setLineId] = React.useState<LineProps | null>(null);
  const [descId, setDescId] = React.useState<DescriptionProps | null>(null);
  const [partTypeId, setPartTypeId] = React.useState<PartTypeProps | null>(null);
  const [newClass, setNewClass] = React.useState<string>('');
  const [newLine, setNewLine] = React.useState<string>('');
  const [newDesc, setNewDesc] = React.useState<string>('');
  const dispatch = useAppDispatch();

  const findClass = (classId: number) => {
    (async () => {
      await catalogApiClient
        .get('api/v1/fitment/parttype/GetClasses')
        .then((response: AxiosResponse<ClassProps[]>) => {
          let findType = response.data.find((x) => x.id === classId);
          if (findType) {
            setClassId(findType);
          }
        });
    })();
  };

  const findLine = (classId: number, lineId: number) => {
    (async () => {
      await catalogApiClient
        .get(`api/v1/fitment/parttype/GetLines?classId=${classId}`)
        .then((response: AxiosResponse<LineProps[]>) => {
          let findType = response.data.find((x) => x.id === lineId);
          if (findType) {
            setLineId(findType);
          }
        });
    })();
  };

  const findDescription = (lineId: number, descId: number) => {
    (async () => {
      await catalogApiClient
        .get(`api/v1/fitment/parttype/GetDescriptions?lineId=${lineId}`)
        .then((response: AxiosResponse<LineProps[]>) => {
          let findType = response.data.find((x) => x.id === descId);
          if (findType) {
            setDescId(findType);
          }
        });
    })();
  };

  const handleSubmit = () => {
    if (!sku || !classId || !lineId || !descId) {
      return;
    }

    const controller = new AbortController();
    (async () => {
      await catalogApiClient
        .get(
          `api/v1/fitment/parttype/SetCLD?sku=${sku}&classId=${classId.id}&lineId=${lineId.id}&descId=${descId.id}`,
          {
            signal: controller.signal,
          },
        )
        .then((response: AxiosResponse<ClassLineDescriptionProp>) => {
          dispatch(alertSuccess(`Class Line Description Saved`));
        })
        .catch(function (err) {
          if (err.message !== 'canceled') {
            dispatch(alertError(`Failed to get class line description for sku. Reason: ${err}`));
          }
        });
    })();
  };

  const handleLoad = () => {
    if (!sku) {
      return;
    }

    const controller = new AbortController();
    (async () => {
      await catalogApiClient
        .get(`api/v1/fitment/parttype/GetCLD?sku=${sku}`, {
          signal: controller.signal,
        })
        .then((response: AxiosResponse<ClassLineDescriptionProp>) => {
          findClass(response.data.classId);
          findLine(response.data.classId, response.data.lineId);
          findDescription(response.data.lineId, response.data.descId);
        })
        .catch(function (err) {
          if (err.message !== 'canceled') {
            dispatch(alertError(`Failed to get class line description for sku. Reason: ${err}`));
          }
        });
    })();
  };

  const handleCreate = () => {
    if (!newClass || !newLine || !newDesc || !partTypeId) {
      return;
    }

    const controller = new AbortController();
    (async () => {
      await catalogApiClient
        .get(
          `api/v1/fitment/parttype/AddCld?className=${newClass}&line=${newLine}&desc=${newDesc}&partType=${partTypeId.partTypeId}`,
          {
            signal: controller.signal,
          },
        )
        .then((response: AxiosResponse<ClassLineDescriptionProp>) => {
          dispatch(alertSuccess(`Class Line Description Created`));
        })
        .catch(function (err) {
          if (err.message !== 'canceled') {
            dispatch(alertError(`Failed to get class line description for sku. Reason: ${err}`));
          }
        });
    })();
  };

  const handlePartTypeChange = (_: any, newValue: PartTypeProps | null) => {
    setPartTypeId(newValue);
  };

  const handleClassChange = (_: any, newValue: ClassProps | null) => {
    setClassId(newValue);
  };

  const handleLineChange = (_: any, newValue: LineProps | null) => {
    setLineId(newValue);
  };

  const handleDescChange = (_: any, newValue: DescriptionProps | null) => {
    setDescId(newValue);
  };

  return (
    <RestrictedAccess type="UI-Access" value="Class Line Description">
      <Card variant="outlined" sx={{ margin: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h5" component="div">
              SKU Class Line Description
            </Typography>
            <TextField
              id="sku"
              value={sku}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setSku(event.target.value);
              }}
              label="Sku"
            />
            <Stack direction="row" spacing={2}>
              <Button color="primary" variant="contained" onClick={handleLoad}>
                Load
              </Button>
              <Button color="success" variant="contained" onClick={handleSubmit}>
                Save
              </Button>
            </Stack>
            <ClassAutoComplete
              value={classId}
              setValue={setClassId}
              handleChange={handleClassChange}
            />
            <LineAutoComplete
              value={lineId}
              classId={classId}
              setValue={setLineId}
              handleChange={handleLineChange}
            />
            <DescriptionAutoComplete
              value={descId}
              lineId={lineId}
              setValue={setDescId}
              handleChange={handleDescChange}
            />
          </Stack>
        </CardContent>
      </Card>
      <Card variant="outlined" sx={{ margin: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h5" component="div">
              Add Class Line Description
            </Typography>

            <RestrictedAccess type="UI-Access" value="Create Class Line Description">
              <TextField
                id="newclass"
                value={newClass}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setNewClass(event.target.value);
                }}
                label="Class"
              />
              <TextField
                id="newline"
                value={newLine}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setNewLine(event.target.value);
                }}
                label="Line"
              />
              <TextField
                id="newDescription"
                value={newDesc}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setNewDesc(event.target.value);
                }}
                label="Description"
              />
              <PartTypeAutoComplete
                value={partTypeId}
                setValue={setPartTypeId}
                handleChange={handlePartTypeChange}
              />
              <Button color="primary" variant="contained" onClick={handleCreate}>
                Create
              </Button>
            </RestrictedAccess>
          </Stack>
        </CardContent>
      </Card>
    </RestrictedAccess>
  );
}
